// MediaUploader.jsx
import React, { useState } from 'react';
import { Spinner, Alert, Button, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import FileUploader from '../FileUpload/FileUploader';
import referenceMediaApi from './referenceMediaApi';
import useReferenceMedia from './useReferenceMedia';
import './media-uploader.css';

const isValidYoutubeUrl = (val) =>
  /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/.test((val || '').trim());

// target: 'product-item' | 'dish'
// allowVideo: adds a "YouTube video" option below the photo grid — video is never uploaded as a
// file (same as the kindergarten gallery), only linked.
const MediaUploader = ({ target, targetId, itemName, allowVideo = false, maxCount = 5, disabled = false }) => {
  const { files, loading, uploading, error, uploadPicked, addYoutubeVideo, removeFile } = useReferenceMedia(target, targetId, itemName);
  const [pending, setPending] = useState([]);
  const [showYoutubeForm, setShowYoutubeForm] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubePreview, setYoutubePreview] = useState(null);
  const [youtubeError, setYoutubeError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  if (!targetId) {
    return (
      <p className="text-muted small mb-0">
        Спочатку збережіть запис — фото{allowVideo ? '/відео' : ''} можна додати після цього, під час редагування.
      </p>
    );
  }

  const photos = files.filter(f => f.source !== 'youtube');
  const videos = files.filter(f => f.source === 'youtube');

  const handlePick = async (filesArr) => {
    setPending(filesArr);
    await uploadPicked(filesArr);
    setPending([]);
  };

  const handleFetchYoutubePreview = async () => {
    if (!isValidYoutubeUrl(youtubeUrl)) {
      setYoutubeError('Введіть коректне посилання на YouTube');
      return;
    }
    setYoutubeError('');
    setYoutubePreview(null);
    setLoadingPreview(true);
    try {
      const data = await referenceMediaApi.getYoutubeOembed(youtubeUrl.trim());
      setYoutubePreview(data);
    } catch (err) {
      setYoutubeError('Не вдалося отримати інформацію про відео. Перевірте посилання.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSaveYoutube = async () => {
    await addYoutubeVideo(youtubeUrl.trim(), youtubePreview?.title);
    setShowYoutubeForm(false);
    setYoutubeUrl('');
    setYoutubePreview(null);
  };

  return (
    <div className="reference-media-uploader">
      {error && <Alert variant="danger" className="py-2 mb-2">{error}</Alert>}

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-2">
          <Spinner animation="border" size="sm" /> Завантаження...
        </div>
      ) : files.length > 0 && (
        <div className="reference-media-grid mb-2">
          {photos.map((f) => (
            <div key={f._id} className="reference-media-item">
              <img src={f.url} alt={f.name} className="reference-media-thumb" />
              {!disabled && (
                <Button variant="danger" size="sm" className="reference-media-remove" onClick={() => removeFile(f._id)} title="Видалити">
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </div>
          ))}
          {videos.map((f) => (
            <div key={f._id} className="reference-media-item">
              <a href={f.youtube_url} target="_blank" rel="noreferrer">
                {f.thumbnail_url ? (
                  <img src={f.thumbnail_url} alt={f.name} className="reference-media-thumb" />
                ) : (
                  <div className="reference-media-thumb reference-media-thumb--broken">
                    <FontAwesomeIcon icon={faYoutube} size="lg" />
                  </div>
                )}
                <span className="reference-media-youtube-badge"><FontAwesomeIcon icon={faYoutube} /></span>
              </a>
              {!disabled && (
                <Button variant="danger" size="sm" className="reference-media-remove" onClick={() => removeFile(f._id)} title="Видалити">
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && files.length < maxCount && (
        <FileUploader
          label=""
          description="Фото JPG, PNG. Максимальний розмір 8 МБ."
          accept="image/*"
          multiple={maxCount > 1}
          maxCount={maxCount - files.length}
          maxSize={8 * 1024 * 1024}
          value={pending}
          onChange={handlePick}
          asBase64={true}
          editable={!uploading}
          disabled={uploading}
        />
      )}

      {!disabled && allowVideo && (
        showYoutubeForm ? (
          <div className="border rounded p-3 mt-2 bg-light">
            {youtubeError && <Alert variant="danger" className="py-2 mb-2">{youtubeError}</Alert>}
            <InputGroup className="mb-2">
              <Form.Control
                type="text"
                placeholder="Посилання на YouTube-відео"
                value={youtubeUrl}
                onChange={(e) => { setYoutubeUrl(e.target.value); setYoutubePreview(null); }}
              />
              <Button variant="outline-secondary" onClick={handleFetchYoutubePreview} disabled={loadingPreview}>
                {loadingPreview ? <Spinner animation="border" size="sm" /> : 'Перевірити'}
              </Button>
            </InputGroup>
            {youtubePreview && (
              <div className="d-flex align-items-center gap-2 mb-2">
                {youtubePreview.thumbnail_url && (
                  <img src={youtubePreview.thumbnail_url} alt="" style={{ width: 80, borderRadius: 4 }} />
                )}
                <span className="small">{youtubePreview.title}</span>
              </div>
            )}
            <div className="d-flex gap-2">
              <Button size="sm" variant="primary" onClick={handleSaveYoutube} disabled={!youtubePreview || uploading}>
                {uploading && <Spinner animation="border" size="sm" className="me-2" />}
                Додати відео
              </Button>
              <Button size="sm" variant="secondary" onClick={() => { setShowYoutubeForm(false); setYoutubeUrl(''); setYoutubePreview(null); }}>
                Скасувати
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="link" size="sm" className="px-0 mt-1" onClick={() => setShowYoutubeForm(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-1" /> Додати відео (YouTube)
          </Button>
        )
      )}
    </div>
  );
};

export default MediaUploader;